import * as React from "react";
import * as t from "io-ts";
import { User } from "@supabase/supabase-js";
import { array, nonEmptyArray, number, option, ord, string } from "fp-ts";
import { constNull, pipe } from "fp-ts/function";
import { contramap } from "fp-ts/Ord";
import { sequenceS } from "fp-ts/Apply";
import {
    Box,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useUser } from "../../utils/supabase/useUser";
import { useQuery } from "../../utils/supabase/useQuery";
import { completed, queryState } from "../../utils/supabase/QueryState";
import { useAlertOnQueryError } from "../../hooks/useAlertOnQueryError";
import { column, columns, join } from "../../utils/pg-ts/pg-ts";
import { emptyArray, optionOrChain, pick } from "../../utils/fp";
import { assertUnreachable } from "../../utils/assertUnreachable";
import { messages } from "../../utils/i18n/i18n";
import { useTranslation } from "../../utils/i18n/lang";
import { Subdomain } from "../../utils/subdomain/subdomain";
import { athleteTable } from "../../domain/user";
import { registrationTable } from "../../domain/registration";
import { events, raceTable } from "../../domain/event";
import {
    raceNumberEq,
    resultMessages,
    resultsWithCategoryTitleView,
    unfinishedReasonLabels,
} from "../../domain/result";
import { organizationsWithSubdomainView } from "../../domain/organization";
import { PageHeading } from "./PageHeading";
import Section, { SectionHeading } from "./Section";
import { CenteredProgress } from "../common/CenteredProgress";
import { LocalDate } from "../common/date/LocalDate";
import { TimeEntryView } from "../common/TimeEntryView";

export const MyResultsList = () =>
    pipe(
        useUser(),
        option.fold(constNull, (user) => <MyResults user={user} />)
    );

const MyResults = (p: { user: User }) => {
    const m = useTranslation(msg);
    const athletesQuery = useMyAthletesWithRegistrations(p.user);
    const athletes = pipe(
        athletesQuery.state.type === "completed"
            ? athletesQuery.state.data
            : emptyArray<MyAthlete>()(),
        array.map((athlete) => ({
            ...athlete,
            // results are public only once published by the organizer
            [registrationAlias]: pipe(
                athlete[registrationAlias],
                array.filter((r) => r[raceAlias][eventAlias].results_published)
            ),
        }))
    );
    const registrations = pipe(
        athletes,
        array.chain((athlete) => athlete[registrationAlias])
    );

    const raceIds = pipe(
        registrations,
        array.map((r) => r.race_id),
        array.uniq(number.Eq),
        array.sort(number.Ord)
    );
    const borns = pipe(
        athletes,
        array.map((athlete) => athlete.born),
        array.uniq(number.Eq),
        array.sort(number.Ord)
    );
    const organizationIds = pipe(
        registrations,
        array.map((r) => r[raceAlias][eventAlias].organization_id),
        array.uniq(number.Eq),
        array.sort(number.Ord)
    );

    const resultsQuery = useQuery(
        `myResults_${raceIds.join("_")}_${borns.join("_")}`,
        (supabase) =>
            supabase
                .from(resultsWithCategoryTitleView.name)
                .select(columns(resultCols))
                .in(column(resultsWithCategoryTitleView, "race_id"), raceIds)
                // a result is bound to an athlete by birth year, see athleteResults
                .in(column(resultsWithCategoryTitleView, "born"), borns),
        t.array(myResultCodec),
        undefined,
        array.isEmpty(raceIds)
    );
    const organizationsQuery = useQuery(
        `myResultsOrganizations_${organizationIds.join("_")}`,
        (supabase) =>
            supabase
                .from(organizationsWithSubdomainView.name)
                .select(columns(organizationCols))
                .in(
                    column(organizationsWithSubdomainView, "id"),
                    organizationIds
                ),
        t.array(organizationCodec),
        undefined,
        array.isEmpty(organizationIds)
    );

    const state = sequenceS(queryState)({
        athletes: athletesQuery.state,
        // skipped queries stay in loading state
        results: array.isEmpty(raceIds)
            ? completed(emptyArray<MyResult>()())
            : resultsQuery.state,
        organizations: array.isEmpty(organizationIds)
            ? completed(emptyArray<Organization>()())
            : organizationsQuery.state,
    });
    useAlertOnQueryError(state);

    switch (state.type) {
        case "loading":
            return <CenteredProgress />;

        case "error":
            return null;

        case "completed":
            const subdomains = new Map(
                state.data.organizations.map((org) => [org.id, org.subdomain])
            );

            return pipe(
                athletes,
                nonEmptyArray.fromArray,
                option.fold(constNull, (athletes) => (
                    <Box component={"section"} sx={{ my: 4 }}>
                        <PageHeading heading={m.heading} />
                        {athletes.map((athlete) => (
                            <AthleteResults
                                key={athlete.id}
                                athlete={athlete}
                                results={athleteResults(
                                    athlete,
                                    state.data.results
                                )}
                                subdomains={subdomains}
                            />
                        ))}
                    </Box>
                ))
            );
    }

    assertUnreachable(state);
};

interface AthleteResultsProps {
    athlete: MyAthlete;
    results: Array<AthleteResult>;
    subdomains: Map<number, Subdomain>;
}

const AthleteResults = (p: AthleteResultsProps) => {
    const m = useTranslation(msg);
    const resultT = useTranslation(resultMessages);
    const unfinishedReasonT = useTranslation(unfinishedReasonLabels);

    return (
        <Section sx={{ mb: 2 }}>
            <SectionHeading
                heading={`${p.athlete.name} (${p.athlete.born})`}
                right={
                    p.athlete.club == null ? null : (
                        <Typography variant={"body2"} color={"text.secondary"}>
                            {p.athlete.club}
                        </Typography>
                    )
                }
            />
            {pipe(
                p.results,
                nonEmptyArray.fromArray,
                option.fold(
                    () => (
                        <Typography variant={"body2"} color={"text.secondary"}>
                            {m.noResults}
                        </Typography>
                    ),
                    (results) => (
                        <TableContainer>
                            <Table size={"small"}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{m.date}</TableCell>
                                        <TableCell>{m.event}</TableCell>
                                        <TableCell>
                                            {resultT.category}
                                        </TableCell>
                                        <TableCell align={"right"}>
                                            {resultT.rank}
                                        </TableCell>
                                        <TableCell align={"right"}>
                                            {resultT.time}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map(({ registration, result }) => {
                                        const race = registration[raceAlias];
                                        const event = race[eventAlias];
                                        const eventTitle = (
                                            <strong>{event.title}</strong>
                                        );

                                        return (
                                            <TableRow key={result.id} hover>
                                                <TableCell>
                                                    <LocalDate
                                                        date={
                                                            event.date ??
                                                            race.start_time
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {pipe(
                                                        p.subdomains.get(
                                                            event.organization_id
                                                        ),
                                                        option.fromNullable,
                                                        option.fold(
                                                            () => eventTitle,
                                                            (subdomain) => (
                                                                <Link
                                                                    href={resultsUrl(
                                                                        subdomain,
                                                                        event.id,
                                                                        registration.race_id
                                                                    )}
                                                                >
                                                                    {eventTitle}
                                                                </Link>
                                                            )
                                                        )
                                                    )}
                                                    <br />
                                                    {race.title}
                                                </TableCell>
                                                <TableCell>
                                                    {result.category_title}
                                                </TableCell>
                                                <TableCell align={"right"}>
                                                    {result.unfinished_reason ==
                                                    null
                                                        ? result.rank
                                                        : unfinishedReasonT[
                                                              result
                                                                  .unfinished_reason
                                                          ]}
                                                </TableCell>
                                                <TableCell align={"right"}>
                                                    {result.time ==
                                                    null ? null : (
                                                        <TimeEntryView
                                                            value={result.time}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                )
            )}
        </Section>
    );
};

// TODO no localhost dev support, same as the live results link in timing
const resultsUrl = (subdomain: Subdomain, eventId: number, raceId: number) =>
    `https://${subdomain}.xathlo.com/event/${eventId}/results?raceId=${raceId}`;

const normalizedName = (name: string) =>
    name.trim().replace(/\s+/g, " ").toLocaleLowerCase();

interface AthleteResult {
    registration: MyRegistration;
    result: MyResult;
}

const ordByRaceStartDesc = pipe(
    string.Ord,
    contramap((r: AthleteResult) => r.registration[raceAlias].start_time),
    ord.reverse
);

// There is no link between a registration and its result, so the result is looked
// up in the registered race by birth year and then by race number, or by name
// the same way the result insert trigger matches organization athletes.
const athleteResults = (
    athlete: MyAthlete,
    results: Array<MyResult>
): Array<AthleteResult> =>
    pipe(
        athlete[registrationAlias],
        array.filterMap((registration) => {
            const candidates = pipe(
                results,
                array.filter(
                    (result) =>
                        result.race_id === registration.race_id &&
                        result.born === athlete.born
                )
            );

            return pipe(
                candidates,
                array.findFirst(
                    (result) =>
                        registration.number != null &&
                        result.number != null &&
                        raceNumberEq.equals(registration.number, result.number)
                ),
                optionOrChain(() =>
                    pipe(
                        candidates,
                        array.findFirst(
                            (result) =>
                                result.name != null &&
                                normalizedName(result.name) ===
                                    normalizedName(athlete.name)
                        )
                    )
                ),
                option.map(
                    (result): AthleteResult => ({ registration, result })
                )
            );
        }),
        array.sort(ordByRaceStartDesc)
    );

const eventAlias = "event";
const eventCols = pipe(
    events.columns,
    pick("id", "title", "date", "organization_id", "results_published")
);
const raceAlias = "race";
const raceCols = pipe(raceTable.columns, pick("title", "start_time"));
const registrationAlias = registrationTable.name;
const registrationCols = pipe(
    registrationTable.columns,
    pick("id", "race_id", "number")
);
const athleteCols = pipe(
    athleteTable.columns,
    pick("id", "name", "born", "club")
);

const myRegistrationCodec = t.type(
    {
        ...registrationCols,
        [raceAlias]: t.type({
            ...raceCols,
            [eventAlias]: t.type(eventCols),
        }),
    },
    "MyRegistration"
);
type MyRegistration = t.TypeOf<typeof myRegistrationCodec>;

const myAthleteCodec = t.type(
    {
        ...athleteCols,
        [registrationAlias]: t.array(myRegistrationCodec),
    },
    "MyAthlete"
);
type MyAthlete = t.TypeOf<typeof myAthleteCodec>;

const useMyAthletesWithRegistrations = (user: User) =>
    useQuery(
        `myAthletesWithRegistrations_${user.id}`,
        (supabase) =>
            supabase
                .from(athleteTable.name)
                .select(
                    columns(
                        athleteCols,
                        join(
                            registrationTable,
                            registrationCols,
                            registrationAlias,
                            false,
                            join(
                                raceTable,
                                raceCols,
                                raceAlias,
                                false,
                                join(events, eventCols, eventAlias)
                            )
                        )
                    )
                )
                .eq(column(athleteTable, "account_id"), user.id)
                .order(column(athleteTable, "name")),
        t.array(myAthleteCodec)
    );

const resultCols = pipe(
    resultsWithCategoryTitleView.columns,
    pick(
        "id",
        "race_id",
        "name",
        "born",
        "number",
        "rank",
        "time",
        "unfinished_reason",
        "category_title"
    )
);
const myResultCodec = t.type(resultCols, "MyResult");
type MyResult = t.TypeOf<typeof myResultCodec>;

const organizationCols = pipe(
    organizationsWithSubdomainView.columns,
    pick("id", "subdomain")
);
const organizationCodec = t.type(organizationCols, "MyResultsOrganization");
type Organization = t.TypeOf<typeof organizationCodec>;

const msg = messages(
    {
        heading: "My results",
        noResults: "No results yet",
        date: "Date",
        event: "Event",
    },
    {
        cs: {
            heading: "Moje výsledky",
            noResults: "Zatím žádné výsledky",
            date: "Datum",
            event: "Akce",
        },
    }
);
